/**
 * Pure copy-selection for the phone-verification step. No React, no I/O — the
 * same split `attemptReason` and `livenessQuality` use, and for the same
 * reason: this is the part with branches worth testing, and the component
 * around it is not.
 *
 * Every branch here answers one question — what can the applicant DO about it?
 * A malformed number is theirs to fix, an unreachable SMS service is not, and a
 * rate limit is neither: it is a wait. Copy that blurs those is copy that
 * strands someone on a screen they cannot leave.
 */
import { CollectionFlowError } from './client';

/** Their API bounds this too; stopping here saves a round trip on a non-code. */
export const MAX_CODE_LEN = 16;

/**
 * Loose E.164 shape check, so a send that cannot succeed never spends a round
 * trip — and never an SMS. The server validates properly and is the authority;
 * this is politeness, not a gate.
 */
export function looksLikeE164(raw: string): boolean {
  return /^\+[1-9]\d{7,14}$/.test(raw.replace(/[\s\-()]/g, ''));
}

/**
 * Map a thrown failure onto a message key.
 *
 * The server's `code` is read before the status because a status alone flattens
 * genuinely different situations: 503 covers both "no SMS service is
 * configured" and "this tenant cannot send", and neither is something the
 * applicant did or can undo.
 */
export function messageKeyFor(error: unknown): string {
  if (!(error instanceof CollectionFlowError)) return 'flow.phone.error.generic';
  switch (error.code) {
    case 'invalid_phone':
      return 'flow.phone.error.badNumber';
    case 'already_verified':
      return 'flow.phone.error.alreadyDone';
    case 'no_pending_verification':
      return 'flow.phone.error.noPending';
    default:
      break;
  }
  if (error.status === 429) return 'flow.phone.error.tooMany';
  if (error.status >= 500) return 'flow.phone.error.unavailable';
  return 'flow.phone.error.generic';
}

/**
 * Map a NON-verified verdict onto a message key.
 *
 * `pending` is the plain wrong guess — the applicant still has attempts, so the
 * copy must invite another try rather than send them for a new code. Every
 * terminal status does the opposite: another try is impossible, and copy that
 * suggests one wastes the applicant's time on a code that can never work.
 */
export function isTerminalVerdict(status: string): boolean {
  return TERMINAL_VERDICTS.has(status);
}

/**
 * Verdicts after which the live session is GONE, not merely unsatisfied.
 *
 * `verify_phone_otp` retires the row to `failed` on every one of these, and
 * `resend_phone_otp` needs a `pending` row — so a resend after any of them
 * cannot succeed, it can only return `no_pending_verification`. The screen has
 * to send the applicant back to ask for a new code instead of offering a button
 * that is guaranteed to fail.
 */
const TERMINAL_VERDICTS = new Set(['expired', 'locked', 'superseded', 'not_found']);

export function verdictKeyFor(status: string): string {
  switch (status) {
    case 'expired':
      return 'flow.phone.error.expired';
    case 'locked':
      return 'flow.phone.error.locked';
    case 'superseded':
    case 'not_found':
      return 'flow.phone.error.noPending';
    default:
      return 'flow.phone.error.wrongCode';
  }
}
